/**
 * Port Service
 * Handles API requests related to port management
 */
import apiClient from './apiClient';

// Define the base URL for port service
const baseUrl = '/api/supplier/port';

const portService = {
  /**
   * Get ports for a tenant
   * @param {string} tenantId - The tenant ID
   * @returns {Promise} - Response with port list
   */
  getPortsByTenant(tenantId) {
    if (!tenantId) {
      console.error('getPortsByTenant: tenantId is required');
      return Promise.reject(new Error('Tenant ID is required'));
    }
    
    return apiClient.get(`${baseUrl}/tenant/${tenantId}`)
      .then(response => response.data);
  },
  
  /**
   * Search for ports with pagination support
   * @param {Object} params - Search parameters
   * @param {number} params.page - Page number (0-based)
   * @param {number} params.pageSize - Number of items per page
   * @param {string} params.sorting - Sorting field and direction
   * @param {string} params.tenantId - Tenant ID to filter by
   * @param {string} params.portID - Port ID to filter by (optional)
   * @param {string} params.searchText - Text to search for (optional)
   * @returns {Promise} - Response with paginated port list
   */
  searchPorts(params) {
    const { tenantId } = params;
    
    if (!tenantId) {
      console.error('searchPorts: tenantId is required');
      return Promise.reject(new Error('Tenant ID is required'));
    }
    
    const requestBody = {
      page: params.page || 0,
      pageSize: params.pageSize || 10,
      sorting: params.sorting || "",
      tenantId: tenantId,
      searchText: params.searchText || ""
    };
    
    if (params.portID) {
      requestBody.portID = params.portID;
    }
    
    return apiClient.post('/api/supplier/port-vs-tenant/search', requestBody)
      .then(response => response.data);
  },
  
  /**
   * Get port selection items by country ID
   * @param {string} countryId - The country ID (UUID)
   * @returns {Promise} - Response with port selection options
   */
  getPortSelectionItems(countryId) {
    if (!countryId) {
      console.error('getPortSelectionItems: countryId is required');
      return Promise.reject(new Error('Country ID is required'));
    }
    
    return apiClient.get(`${baseUrl}/selection-items/${countryId}`)
      .then(response => response.data);
  },
  
  /**
   * Get every port grouped by country in a single request.
   * Avoids issuing one request per country when populating or bulk-selecting the picker.
   * @returns {Promise<Array>} - [{ countryId, countryName, ports: [{ id, name, portCode }] }]
   */
  getAllPortSelectionItems() {
    return apiClient.get(`${baseUrl}/selection-items`)
      .then(response => response.data);
  },

  /**
   * Get country selection items
   * @param {string} searchText - Optional search text to filter countries
   * @returns {Promise} - Response with country selection options
   */
  getCountrySelectionItems(searchText = '') {
    const params = searchText ? { searchText } : {};
    return apiClient.get('/api/supplier/country/selection-items', { params })
      .then(response => response.data);
  },
  
  /**
   * Save ports for a tenant
   * @param {string} tenantId - The tenant ID
   * @param {Array} ports - Array of port objects to save
   * @returns {Promise} - Response with saved ports
   */
  savePorts(tenantId, ports, options = {}) {
    if (!tenantId) {
      console.error('savePorts: tenantId is required');
      return Promise.reject(new Error('Tenant ID is required'));
    }
    
    if (!ports || !Array.isArray(ports)) {
      console.error('savePorts: ports must be an array');
      return Promise.reject(new Error('Ports are required'));
    }
    
    // Extract port IDs and format request body as required by the API
    const portIDs = ports.map(port => port.id || port);
    const requestBody = {
      portIDs,
      // portIDs is the complete desired selection, so the API must also drop
      // the mappings the user deselected.
      replaceExisting: options.replaceExisting !== false,
      // The saved rows are not used by the caller; skipping them keeps large saves fast.
      skipResultMapping: options.skipResultMapping !== false
    };
    
    return apiClient.post(`/api/supplier/port-vs-tenant`, requestBody)
      .then(response => response.data);
  },
  
  /**
   * Delete a port association for a tenant
   * @param {string} portId - The port ID to delete
   * @returns {Promise} - Response with deletion result
   */
  deletePort(portId) {
    if (!portId) {
      console.error('deletePort: portId is required');
      return Promise.reject(new Error('Port ID is required'));
    }
    
    // Use port-vs-tenant endpoint to remove port association
    return apiClient.delete(`/api/supplier/port-vs-tenant/${portId}`)
      .then(response => response.data);
  },
  
  /**
   * Get a port by ID
   * @param {string} portId - Port ID
   * @returns {Promise} - Response with port details
   */
  getPortById(portId) {
    if (!portId) {
      console.error('getPortById: portId is required');
      return Promise.reject(new Error('Port ID is required'));
    }
    
    return apiClient.get(`${baseUrl}/${portId}`)
      .then(response => response.data);
  }
};

export default portService; 